import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import {
  Upload, FileText, CheckCircle2, Clock, AlertCircle, LogOut,
  Loader2, Inbox, HandCoins, Plus, Landmark, X, MessageSquare,
  Sparkles, Download, Activity, Send, Paperclip, Wand2, ChevronRight, HelpCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import ProductTour from "@/components/ProductTour";
import ClientAdvisory from "@/components/advisory/ClientAdvisory";

// Explicit lifecycle for client-facing statement status.
// Backend statuses: "processing" (queued/parsing) → "parsed" | "failed".
// We surface a friendly UX layer on top: RECEIVED → PARSING → PARSED / FAILED.
const STATUS_META = {
  processing: {
    label: "Received · parsing",
    tone: "info",
    Icon: Loader2,
    spin: true,
    hint: "We've got your statement. Our AI is reading it now — this usually takes 10–60 seconds.",
  },
  parsed: {
    label: "Parsed successfully",
    tone: "success",
    Icon: CheckCircle2,
    hint: "Your accountant has been notified and can now review the transactions.",
  },
  failed: {
    label: "Upload failed",
    tone: "error",
    Icon: AlertCircle,
    hint: "The file couldn't be parsed. Please check the format or re-upload.",
  },
};

const TONE_CLASSES = {
  info:    { bg: "#DBEAFE", text: "#1E3A8A", dot: "#3B82F6" },
  success: { bg: "#D1FAE5", text: "#065F46", dot: "#10B981" },
  error:   { bg: "#FEE2E2", text: "#991B1B", dot: "#DC2626" },
};

function StatusPill({ status, testid }) {
  const meta = STATUS_META[status] || STATUS_META.processing;
  const tone = TONE_CLASSES[meta.tone];
  const Icon = meta.Icon;
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1"
      style={{ background: tone.bg, color: tone.text }}
      data-testid={testid}
    >
      <Icon size={12} className={meta.spin ? "animate-spin" : ""} />
      {meta.label}
    </span>
  );
}

export default function ClientPortal() {
  const { user, tenant, logout, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState("");
  const [statements, setStatements] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [justReceived, setJustReceived] = useState(null); // {id, name} for the acknowledgement banner
  const [addOpen, setAddOpen] = useState(false);
  const [creatingBank, setCreatingBank] = useState(false);
  const [newBank, setNewBank] = useState({
    name: "",
    bank_name: "",
    account_number_masked: "",
    account_type: "checking",
    opening_balance: "",
  });
  const fileRef = useRef(null);

  // New: activity feed, shared reports, messages
  const [activity, setActivity] = useState([]);
  const [reports, setReports] = useState([]);
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgDraft, setMsgDraft] = useState("");
  const [msgSending, setMsgSending] = useState(false);
  const [msgAttachments, setMsgAttachments] = useState([]);
  const msgFileRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Onboarding wizard: shown once per client (localStorage flag). Auto-hides
  // as soon as they've completed each step, so returning users don't see it.
  // Two localStorage keys:
  //   *_done_*    → permanent dismissal (Hide for good)
  //   *_snooze_*  → ISO timestamp; wizard stays hidden until then
  const onboardingDoneKey = user ? `client_onboarding_done_${user.id}` : "client_onboarding_done";
  const onboardingSnoozeKey = user ? `client_onboarding_snooze_${user.id}` : "client_onboarding_snooze";
  const [onboardingSeen, setOnboardingSeen] = useState(() => {
    try {
      if (localStorage.getItem(onboardingDoneKey) === "1") return true;
      const snoozeUntil = localStorage.getItem(onboardingSnoozeKey);
      if (snoozeUntil && Date.parse(snoozeUntil) > Date.now()) return true;
      return false;
    } catch { return true; }
  });
  const dismissOnboarding = () => {
    try { localStorage.setItem(onboardingDoneKey, "1"); } catch { /* ignore */ }
    setOnboardingSeen(true);
  };
  const snoozeOnboarding = () => {
    try {
      const until = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
      localStorage.setItem(onboardingSnoozeKey, until);
    } catch { /* ignore */ }
    setOnboardingSeen(true);
  };

  // Reply-in-email deep-link: /client?open_messages=1 auto-opens the panel
  // and lands the client directly on the conversation with their accountant.
  useEffect(() => {
    if (searchParams.get("open_messages") === "1") {
      openMessages();
      // Clear the query so refreshes don't repeatedly re-open.
      const next = new URLSearchParams(searchParams);
      next.delete("open_messages");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!user) refreshMe();
    // eslint-disable-next-line
  }, []);

  const loadBanks = async () => {
    try {
      const res = await api.get("/bank-accounts");
      setBanks(res.data);
      if (res.data.length && !selectedBank) setSelectedBank(res.data[0].id);
    } catch (err) {
      toast.error("Could not load bank accounts");
    }
  };

  const loadStatements = async () => {
    try {
      const res = await api.get("/statements", { params: { limit: 50 } });
      setStatements(res.data);
    } catch (err) { /* silent */ }
  };

  useEffect(() => {
    loadBanks();
    loadStatements();
    loadActivity();
    loadReports();
    loadThread();
    // Auto-refresh so the client sees "Received · parsing" flip to "Parsed"
    // without needing to reload the page.
    const id = setInterval(() => { loadStatements(); loadActivity(); loadReports(); loadThread(); }, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, []);

  const loadActivity = async () => {
    try {
      const r = await api.get("/client-activity", { params: { limit: 20 } });
      setActivity(r.data.items || []);
    } catch { /* silent */ }
  };

  const loadReports = async () => {
    try {
      const r = await api.get("/shared-reports");
      setReports(r.data || []);
    } catch { /* silent */ }
  };

  const loadThread = async () => {
    try {
      const r = await api.get("/threads");
      const t = (r.data.items || [])[0];
      if (t) {
        setThread(t);
        if (msgOpen) {
          const mr = await api.get(`/threads/${t.id}/messages`);
          setMessages(mr.data.items || []);
        }
      }
    } catch { /* silent */ }
  };

  const openMessages = async () => {
    if (!thread) await loadThread();
    setMsgOpen(true);
    try {
      const mr = await api.get(`/threads/${thread.id}/messages`);
      setMessages(mr.data.items || []);
      await loadThread();
    } catch { /* ignore */ }
  };

  const uploadMsgAttachment = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/messages/attachments", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMsgAttachments((prev) => [...prev, r.data]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Attachment upload failed");
    } finally {
      if (msgFileRef.current) msgFileRef.current.value = "";
    }
  };

  const sendMessage = async () => {
    if (!thread) return;
    const body = msgDraft.trim();
    if (!body && msgAttachments.length === 0) return;
    setMsgSending(true);
    try {
      await api.post(`/threads/${thread.id}/messages`, {
        body,
        attachments: msgAttachments,
        refs: [],
      });
      setMsgDraft("");
      setMsgAttachments([]);
      const mr = await api.get(`/threads/${thread.id}/messages`);
      setMessages(mr.data.items || []);
      await loadThread();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally {
      setMsgSending(false);
    }
  };

  const downloadReport = async (sr) => {
    try {
      const r = await api.get(`/shared-reports/${sr.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${sr.title.replace(/\s+/g, "_").toLowerCase()}.${sr.file_type}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!selectedBank) {
      toast.error("Pick a bank account first");
      return;
    }
    const okExt = /\.(pdf|csv|xlsx?|)$/i.test(file.name);
    if (!okExt) {
      toast.error("Only PDF, CSV, or XLSX files are accepted");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("bank_account_id", selectedBank);
      fd.append("file", file);
      const res = await api.post("/statements/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Immediate acknowledgement — this is what the client sees BEFORE parsing finishes.
      setJustReceived({ id: res.data.statement_id, name: file.name });
      toast.success(`We received "${file.name}" — parsing now.`);
      if (fileRef.current) fileRef.current.value = "";
      await loadStatements();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : detail?.message || "Upload failed. Please try again.";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const onLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const submitNewBank = async (e) => {
    e.preventDefault();
    if (!newBank.name.trim() || !newBank.bank_name.trim()) {
      toast.error("Nickname and bank name are required");
      return;
    }
    setCreatingBank(true);
    try {
      // Mask the last-4 the way the rest of the app does it — accepts a full
      // account number or just the last-4, always stores "••••1234".
      const raw = newBank.account_number_masked.trim();
      const last4 = raw.replace(/\D/g, "").slice(-4);
      const masked = last4 ? `••••${last4}` : "";
      const payload = {
        name: newBank.name.trim(),
        bank_name: newBank.bank_name.trim(),
        account_number_masked: masked,
        account_type: newBank.account_type,
        opening_balance: parseFloat(newBank.opening_balance) || 0,
      };
      const res = await api.post("/bank-accounts", payload);
      toast.success(`"${res.data.name}" added — you can now upload statements to it.`);
      setAddOpen(false);
      setNewBank({ name: "", bank_name: "", account_number_masked: "", account_type: "checking", opening_balance: "" });
      await loadBanks();
      // Auto-select the newly created bank so the very next upload uses it.
      setSelectedBank(res.data.id);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : detail?.message || "Could not add bank account";
      toast.error(msg);
    } finally {
      setCreatingBank(false);
    }
  };

  // Belt-and-suspenders: if a non-client account somehow lands here, bounce back.
  if (user && user.role !== "client") {
    return <Navigate to="/app/dashboard" replace />;
  }

  const bank = banks.find((b) => b.id === selectedBank);
  const summary = statements.reduce(
    (acc, s) => {
      acc[s.status] = (acc[s.status] || 0) + 1;
      return acc;
    },
    { processing: 0, parsed: 0, failed: 0 },
  );

  // Firm branding — falls back to the platform's navy default so tenants
  // that haven't customized anything still look intentional.
  const brandColor = tenant?.brand_color || "#0B2545";
  const logoSrc = tenant?.logo_base64
    ? (tenant.logo_base64.startsWith("data:") ? tenant.logo_base64 : `data:image/png;base64,${tenant.logo_base64}`)
    : null;

  return (
    <div className="min-h-screen" style={{ background: "var(--ll-bg)" }} data-testid="client-portal">
      <ProductTour />
      {/* Header */}
      <header
        className="w-full border-b text-white"
        style={{ borderColor: "var(--ll-border)", background: brandColor }}
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/client" className="flex items-center gap-3" data-testid="client-logo">
            {logoSrc ? (
              <img
                src={logoSrc}
                alt={tenant?.name || "Firm logo"}
                className="w-9 h-9 object-contain bg-white p-1"
                data-testid="client-brand-logo"
              />
            ) : (
              <div className="w-9 h-9 grid place-items-center bg-white" style={{ color: brandColor }}>
                <span className="font-display font-bold text-lg">
                  {(tenant?.name || "F").trim().charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <div>
              <div className="font-display font-bold text-lg leading-tight" data-testid="client-brand-name">
                {tenant?.name || "Financials Ready"}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-white/60">Client portal</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            {user && (
              <div className="text-right hidden sm:block">
                <div className="text-xs font-medium" data-testid="client-user-name">{user.name}</div>
                <div className="text-[10px] uppercase tracking-widest text-white/60">Client</div>
              </div>
            )}
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("ll:tour:show"))}
              data-testid="client-launch-tour-btn"
              className="text-xs px-3 py-1.5 border border-white/20 hover:bg-white/10 flex items-center gap-1.5"
              title="Replay the welcome tour"
            >
              <HelpCircle size={12} /> Tour
            </button>
            <button
              onClick={onLogout}
              data-testid="client-logout-btn"
              className="text-xs px-3 py-1.5 border border-white/20 hover:bg-white/10 flex items-center gap-1.5"
            >
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* Hero */}
        <section>
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] mb-2">
            Welcome, {user?.name?.split(" ")[0] || "there"}
          </div>
          <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight leading-tight">
            Drop a statement.<br />We&apos;ll take it from here.
          </h1>
          <p className="mt-3 text-sm text-[var(--ll-ink-soft)] max-w-xl">
            Upload your bank statement below (PDF, CSV, or XLSX). We&apos;ll acknowledge receipt
            immediately, auto-parse the transactions, and let your accountant know it&apos;s ready
            to review.
          </p>
        </section>

        {/* Firm-authored welcome */}
        {tenant?.portal_welcome_message && (
          <section
            className="border p-4 flex items-start gap-3"
            style={{ background: "var(--ll-mint)", borderColor: "var(--ll-border)" }}
            data-testid="client-portal-welcome"
          >
            <Sparkles size={16} className="mt-0.5 shrink-0" style={{ color: brandColor }} />
            <div className="text-sm whitespace-pre-wrap" data-testid="client-portal-welcome-text">
              {tenant.portal_welcome_message}
            </div>
          </section>
        )}

        {/* Onboarding wizard — shown only until the client's first parsed upload */}
        {!onboardingSeen && statements.length === 0 && (
          <section
            className="border bg-white overflow-hidden"
            style={{ borderColor: "var(--ll-border)" }}
            data-testid="client-onboarding-wizard"
          >
            <div
              className="px-5 py-4 flex items-center justify-between text-white"
              style={{ background: brandColor }}
            >
              <div className="flex items-center gap-2">
                <Wand2 size={16} />
                <div>
                  <div className="text-[10px] uppercase tracking-widest opacity-75">Getting started</div>
                  <div className="font-display font-bold text-lg">Your 3-step first upload</div>
                </div>
              </div>
              <button
                onClick={dismissOnboarding}
                data-testid="client-onboarding-dismiss"
                aria-label="Dismiss tour"
                className="text-white/80 hover:text-white"
              >
                <X size={16} />
              </button>
            </div>
            <ol className="divide-y" style={{ borderColor: "var(--ll-border)" }}>
              {[
                {
                  n: 1,
                  done: banks.length > 0,
                  title: "Add your bank account",
                  body: "Nickname it (e.g. \"Operating checking\"), pick a bank name and account type. Only the last 4 digits are stored.",
                  cta: "Add a bank",
                  onClick: () => setAddOpen(true),
                  testid: "onboarding-step-1",
                },
                {
                  n: 2,
                  done: statements.length > 0,
                  title: "Upload your first statement",
                  body: "Drag any PDF, CSV, or XLSX file into the upload zone below. We'll parse it automatically — no manual entry needed.",
                  cta: "Choose a file",
                  onClick: () => fileRef.current?.click(),
                  testid: "onboarding-step-2",
                },
                {
                  n: 3,
                  done: false,
                  title: "We'll take it from here",
                  body: "You'll see the parsed status appear here, and your accountant will be notified instantly. If you need anything, use the Message button at the top.",
                  cta: "Message accountant",
                  onClick: openMessages,
                  testid: "onboarding-step-3",
                },
              ].map((step) => (
                <li
                  key={step.n}
                  data-testid={step.testid}
                  className="px-5 py-4 flex items-start gap-4"
                  style={{ borderColor: "var(--ll-border)" }}
                >
                  <div
                    className="w-9 h-9 grid place-items-center shrink-0 rounded-full text-sm font-bold"
                    style={{
                      background: step.done ? "#D1FAE5" : "var(--ll-mint)",
                      color: step.done ? "#065F46" : brandColor,
                    }}
                  >
                    {step.done ? <CheckCircle2 size={16} /> : step.n}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">
                      {step.title}
                      {step.done && <span className="ml-2 text-[10px] uppercase tracking-widest text-[#065F46]">Done</span>}
                    </div>
                    <div className="text-xs text-[var(--ll-ink-soft)] mt-1">{step.body}</div>
                  </div>
                  <button
                    onClick={step.onClick}
                    disabled={step.done && step.n < 3}
                    className="text-xs px-3 py-1.5 flex items-center gap-1 border shrink-0 hover:bg-[var(--ll-mint)] disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ borderColor: "var(--ll-border)" }}
                    data-testid={`${step.testid}-cta`}
                  >
                    {step.cta} <ChevronRight size={11} />
                  </button>
                </li>
              ))}
            </ol>
            <div className="px-5 py-3 border-t bg-[var(--ll-bg-sidebar)] text-[11px] text-[var(--ll-ink-soft)] flex items-center justify-between flex-wrap gap-2" style={{ borderColor: "var(--ll-border)" }}>
              <span>You can revisit this by clearing your browser storage.</span>
              <div className="flex items-center gap-3">
                <button
                  onClick={snoozeOnboarding}
                  data-testid="client-onboarding-snooze"
                  className="hover:text-[var(--ll-ink)]"
                >
                  Skip for now (24h)
                </button>
                <button
                  onClick={dismissOnboarding}
                  data-testid="client-onboarding-hide"
                  className="hover:text-[var(--ll-ink)]"
                >
                  Hide for good
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Messages toggle button */}
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={openMessages}
            data-testid="client-open-messages-btn"
            className="inline-flex items-center gap-2 px-4 py-2 border text-sm hover:bg-[var(--ll-mint)] transition-colors"
            style={{ borderColor: "var(--ll-border)" }}
          >
            <MessageSquare size={14} />
            Message your accountant
            {thread?.unread_for_client > 0 && (
              <span
                className="min-w-[18px] h-[18px] px-1 rounded-full text-white text-[10px] font-bold grid place-items-center"
                style={{ background: "var(--ll-ochre)" }}
                data-testid="client-thread-unread-count"
              >
                {thread.unread_for_client}
              </span>
            )}
          </button>
          {reports.length > 0 && (
            <div className="text-xs text-[var(--ll-ink-soft)] flex items-center gap-1.5">
              <FileText size={12} /> {reports.length} report{reports.length === 1 ? "" : "s"} available below
            </div>
          )}
        </div>

        {/* Acknowledgement banner — visible right after upload */}
        {justReceived && (
          <div
            className="border p-4 flex items-start gap-3"
            style={{ background: "#DBEAFE", borderColor: "#93C5FD" }}
            data-testid="client-received-banner"
          >
            <div className="w-8 h-8 grid place-items-center bg-white shrink-0">
              <Loader2 size={16} className="text-[#1E40AF] animate-spin" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-[#1E3A8A]">
                We&apos;ve received &ldquo;{justReceived.name}&rdquo; — parsing now.
              </div>
              <div className="text-xs text-[#1E3A8A]/80 mt-0.5">
                You&apos;ll see it appear in your history below once parsing completes.
                Your accountant will be notified automatically.
              </div>
            </div>
            <button
              onClick={() => setJustReceived(null)}
              className="text-xs text-[#1E3A8A]/70 hover:text-[#1E3A8A]"
              data-testid="client-received-dismiss"
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        )}

        {/* Upload card */}
        <section
          className="bg-white border p-6 sm:p-8"
          style={{ borderColor: "var(--ll-border)" }}
          data-testid="client-upload-card"
        >
          <div className="flex items-center justify-between mb-2">
            <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)]">
              Bank account
            </label>
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              data-testid="client-add-bank-btn"
              className="text-xs flex items-center gap-1 px-2 py-1 border hover:bg-[var(--ll-mint)] transition-colors"
              style={{ borderColor: "var(--ll-border)" }}
            >
              <Plus size={12} /> Add bank
            </button>
          </div>
          {banks.length === 0 ? (
            <div
              className="border border-dashed p-4 text-center text-sm mb-5"
              style={{ borderColor: "var(--ll-border)", background: "var(--ll-bg-sidebar)" }}
              data-testid="client-no-banks"
            >
              <Landmark size={18} className="mx-auto mb-2 text-[var(--ll-ink-muted)]" />
              <div className="text-[var(--ll-ink)] font-medium mb-1">No bank accounts yet</div>
              <div className="text-xs text-[var(--ll-ink-soft)] mb-3">Add one to start uploading statements.</div>
              <button
                type="button"
                onClick={() => setAddOpen(true)}
                data-testid="client-add-bank-cta"
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-[var(--ll-ink)] text-white hover:bg-[var(--ll-ink-hover)]"
              >
                <Plus size={12} /> Add bank account
              </button>
            </div>
          ) : (
            <select
              value={selectedBank}
              onChange={(e) => setSelectedBank(e.target.value)}
              data-testid="client-bank-select"
              className="w-full px-3 py-2.5 border bg-white text-sm mb-5"
              style={{ borderColor: "var(--ll-border)" }}
            >
              {banks.map((b) => (
                <option key={b.id} value={b.id}>{b.name}{b.bank_name ? ` · ${b.bank_name}` : ""}</option>
              ))}
            </select>
          )}

          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const f = e.dataTransfer.files?.[0];
              if (f) handleFile(f);
            }}
            className="border-2 border-dashed p-8 sm:p-10 text-center transition-colors"
            style={{
              borderColor: dragOver ? "var(--ll-ink)" : "var(--ll-border)",
              background: dragOver ? "var(--ll-mint)" : "transparent",
            }}
            data-testid="client-dropzone"
          >
            <div className="w-14 h-14 mx-auto grid place-items-center bg-[var(--ll-mint)] mb-4">
              {uploading
                ? <Loader2 size={22} className="animate-spin text-[var(--ll-ink)]" />
                : <Upload size={22} className="text-[var(--ll-ink)]" />}
            </div>
            <div className="font-display font-bold text-lg mb-1">
              {uploading ? "Uploading…" : "Drop your statement here"}
            </div>
            <div className="text-sm text-[var(--ll-ink-soft)] mb-5">
              or click below · PDF, CSV, or XLSX · up to 20 MB
            </div>
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              accept=".pdf,.csv,.xlsx,.xls"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="client-file-input"
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading || !selectedBank}
              data-testid="client-choose-file-btn"
              style={{ background: brandColor }}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              <Upload size={14} />
              {uploading ? "Uploading…" : "Choose a file"}
            </button>
          </div>
        </section>

        {/* Summary strip */}
        {statements.length > 0 && (
          <section className="grid grid-cols-3 gap-3" data-testid="client-summary">
            <SummaryCell label="In progress" value={summary.processing} tone="info" testid="client-summary-processing" />
            <SummaryCell label="Successful" value={summary.parsed} tone="success" testid="client-summary-parsed" />
            <SummaryCell label="Failed" value={summary.failed} tone="error" testid="client-summary-failed" />
          </section>
        )}

        {/* History */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Inbox size={16} className="text-[var(--ll-ink-muted)]" />
            <div className="font-display font-bold text-lg">Your uploads</div>
            <div className="text-xs text-[var(--ll-ink-soft)] ml-auto">{statements.length} total</div>
          </div>

          {statements.length === 0 ? (
            <div
              className="bg-white border p-10 text-center"
              style={{ borderColor: "var(--ll-border)" }}
              data-testid="client-history-empty"
            >
              <FileText size={28} className="mx-auto mb-3 text-[var(--ll-ink-muted)]" />
              <div className="font-medium text-sm">No uploads yet</div>
              <div className="text-xs text-[var(--ll-ink-soft)] mt-1">
                Drop your first statement above — you&apos;ll see it appear here.
              </div>
            </div>
          ) : (
            <div
              className="bg-white border overflow-hidden"
              style={{ borderColor: "var(--ll-border)" }}
              data-testid="client-history"
            >
              {statements.map((s, i) => {
                const meta = STATUS_META[s.status] || STATUS_META.processing;
                return (
                  <div
                    key={s.id}
                    className={`px-5 py-4 flex items-start gap-4 ${i < statements.length - 1 ? "border-b" : ""}`}
                    style={{ borderColor: "var(--ll-border)" }}
                    data-testid={`client-statement-row-${s.id}`}
                  >
                    <div className="w-9 h-9 shrink-0 grid place-items-center bg-[var(--ll-bg-sidebar)]">
                      <FileText size={16} className="text-[var(--ll-ink-muted)]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="text-sm font-medium truncate max-w-full" data-testid={`client-statement-name-${s.id}`}>
                          {s.file_name}
                        </div>
                        <StatusPill status={s.status} testid={`client-statement-status-${s.id}`} />
                      </div>
                      <div className="text-xs text-[var(--ll-ink-soft)] mt-1">
                        Uploaded {fmtDate(s.uploaded_at)}
                        {s.status === "parsed" && s.transaction_count > 0 && (
                          <> · <strong>{s.transaction_count}</strong> transactions imported</>
                        )}
                      </div>
                      <div className="text-xs text-[var(--ll-ink-soft)] mt-2">
                        {s.status === "failed" && s.error_message
                          ? <span className="text-[#991B1B]">{s.error_message}</span>
                          : meta.hint}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <ClientAdvisory />

        {/* Shared reports from firm */}
        {reports.length > 0 && (
          <section data-testid="client-reports-section">
            <div className="flex items-center gap-2 mb-3">
              <FileText size={16} className="text-[var(--ll-ink-muted)]" />
              <div className="font-display font-bold text-lg">Reports from your accountant</div>
              <div className="text-xs text-[var(--ll-ink-soft)] ml-auto">{reports.length} total</div>
            </div>
            <div className="bg-white border overflow-hidden" style={{ borderColor: "var(--ll-border)" }}>
              {reports.map((r, i) => (
                <div
                  key={r.id}
                  className={`px-5 py-4 flex items-start gap-4 ${i < reports.length - 1 ? "border-b" : ""}`}
                  style={{ borderColor: "var(--ll-border)" }}
                  data-testid={`client-report-row-${r.id}`}
                >
                  <div className="w-9 h-9 shrink-0 grid place-items-center" style={{ background: "var(--ll-mint)", color: brandColor }}>
                    <FileText size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{r.title}
                      <span className="ml-2 text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">{r.file_type}</span>
                    </div>
                    <div className="text-xs text-[var(--ll-ink-soft)] mt-0.5">
                      {r.period_start || r.period_end ? `${r.period_start || ""} → ${r.period_end || "today"} · ` : ""}
                      Shared by {r.created_by_name || "your accountant"} · {fmtDate(r.created_at)}
                    </div>
                    {r.note && (
                      <div className="text-xs text-[var(--ll-ink)] mt-2 italic">&ldquo;{r.note}&rdquo;</div>
                    )}
                  </div>
                  <button
                    onClick={() => downloadReport(r)}
                    data-testid={`client-report-download-${r.id}`}
                    className="text-xs px-3 py-1.5 flex items-center gap-1.5 text-white hover:opacity-90"
                    style={{ background: brandColor }}
                  >
                    <Download size={12} /> Download
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Activity feed */}
        {activity.length > 0 && (
          <section data-testid="client-activity-section">
            <div className="flex items-center gap-2 mb-3">
              <Activity size={16} className="text-[var(--ll-ink-muted)]" />
              <div className="font-display font-bold text-lg">Recent activity</div>
              <div className="text-xs text-[var(--ll-ink-soft)] ml-auto">last {activity.length}</div>
            </div>
            <div className="bg-white border overflow-hidden" style={{ borderColor: "var(--ll-border)" }}>
              {activity.map((a, i) => (
                <div
                  key={a.id}
                  className={`px-5 py-3 flex items-start gap-3 ${i < activity.length - 1 ? "border-b" : ""}`}
                  style={{ borderColor: "var(--ll-border)" }}
                  data-testid={`client-activity-row-${a.id}`}
                >
                  <div
                    className="w-2 h-2 rounded-full mt-2 shrink-0"
                    style={{ background: {
                      upload: "#3B82F6",
                      parsed: "#10B981",
                      parse_failed: "#DC2626",
                      bank_assigned: "#F59E0B",
                      reminder_sent: "#8B5CF6",
                      report_shared: brandColor,
                      message: brandColor,
                    }[a.kind] || "#94A3B8" }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{a.title}</div>
                    {a.body && <div className="text-xs text-[var(--ll-ink-soft)] mt-0.5">{a.body}</div>}
                    <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mt-1">
                      {fmtDate(a.created_at)}{a.actor_name ? ` · by ${a.actor_name}` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <footer className="pt-6 pb-4 text-[11px] text-[var(--ll-ink-muted)] flex items-center gap-2 flex-wrap">
          <HandCoins size={12} />
          Questions? Message us or email
          {tenant?.portal_support_email ? (
            <a
              href={`mailto:${tenant.portal_support_email}`}
              className="underline underline-offset-2"
              style={{ color: brandColor }}
              data-testid="client-portal-support-email"
            >{tenant.portal_support_email}</a>
          ) : (
            <> your accountant directly</>
          )}.
        </footer>
      </main>

      {addOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4"
          onClick={() => !creatingBank && setAddOpen(false)}
          data-testid="client-add-bank-modal"
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitNewBank}
            className="bg-white border w-full max-w-md p-6"
            style={{ borderColor: "var(--ll-ink)" }}
          >
            <div className="flex items-start justify-between mb-1">
              <h2 className="font-display font-bold text-2xl flex items-center gap-2">
                <Landmark size={20} /> Add bank account
              </h2>
              <button
                type="button"
                onClick={() => setAddOpen(false)}
                disabled={creatingBank}
                data-testid="client-add-bank-close"
                aria-label="Close"
                className="text-[var(--ll-ink-soft)] hover:text-[var(--ll-ink)] disabled:opacity-50"
              >
                <X size={16} />
              </button>
            </div>
            <p className="text-sm text-[var(--ll-ink-soft)] mb-5">
              Once added, you can start uploading statements to it right away.
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Nickname</label>
                <input
                  required
                  value={newBank.name}
                  onChange={(e) => setNewBank({ ...newBank, name: e.target.value })}
                  placeholder="e.g. Operating checking"
                  data-testid="client-newbank-name"
                  className="w-full px-3 py-2 border text-sm"
                  style={{ borderColor: "var(--ll-border)" }}
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Bank name</label>
                <input
                  required
                  value={newBank.bank_name}
                  onChange={(e) => setNewBank({ ...newBank, bank_name: e.target.value })}
                  placeholder="e.g. Chase, Bank of America"
                  data-testid="client-newbank-bank"
                  className="w-full px-3 py-2 border text-sm"
                  style={{ borderColor: "var(--ll-border)" }}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Last 4 digits</label>
                  <input
                    value={newBank.account_number_masked}
                    onChange={(e) => setNewBank({ ...newBank, account_number_masked: e.target.value })}
                    placeholder="1234"
                    maxLength={20}
                    data-testid="client-newbank-last4"
                    className="w-full px-3 py-2 border text-sm"
                    style={{ borderColor: "var(--ll-border)" }}
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Type</label>
                  <select
                    value={newBank.account_type}
                    onChange={(e) => setNewBank({ ...newBank, account_type: e.target.value })}
                    data-testid="client-newbank-type"
                    className="w-full px-3 py-2 border bg-white text-sm"
                    style={{ borderColor: "var(--ll-border)" }}
                  >
                    <option value="checking">Checking</option>
                    <option value="savings">Savings</option>
                    <option value="credit_card">Credit card</option>
                    <option value="loan">Loan</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Opening balance (optional)</label>
                <input
                  type="number"
                  step="0.01"
                  value={newBank.opening_balance}
                  onChange={(e) => setNewBank({ ...newBank, opening_balance: e.target.value })}
                  placeholder="0.00"
                  data-testid="client-newbank-opening"
                  className="w-full px-3 py-2 border text-sm"
                  style={{ borderColor: "var(--ll-border)" }}
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => setAddOpen(false)}
                disabled={creatingBank}
                data-testid="client-newbank-cancel"
                className="px-4 py-2 border text-sm"
                style={{ borderColor: "var(--ll-border)" }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creatingBank}
                data-testid="client-newbank-submit"
                className="px-4 py-2 bg-[var(--ll-ink)] text-white hover:bg-[var(--ll-ink-hover)] text-sm disabled:opacity-50 flex items-center gap-1.5"
              >
                {creatingBank ? <><Loader2 size={13} className="animate-spin" /> Adding…</> : <><Plus size={13} /> Add bank</>}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Messages slide-over */}
      {msgOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex justify-end"
          onClick={() => !msgSending && setMsgOpen(false)}
          data-testid="client-messages-panel"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-md h-full flex flex-col shadow-xl"
          >
            <div
              className="px-5 py-3 border-b flex items-center justify-between text-white"
              style={{ background: brandColor, borderColor: "var(--ll-border)" }}
            >
              <div className="flex items-center gap-2">
                <MessageSquare size={16} />
                <div>
                  <div className="font-display font-bold text-sm">Message your accountant</div>
                  <div className="text-[10px] uppercase tracking-widest opacity-75">{tenant?.name || "Firm"}</div>
                </div>
              </div>
              <button
                onClick={() => setMsgOpen(false)}
                aria-label="Close"
                data-testid="client-messages-close"
                className="text-white/80 hover:text-white"
              >
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="client-messages-list">
              {messages.length === 0 ? (
                <div className="text-center text-sm text-[var(--ll-ink-soft)] py-10">
                  Say hi to your accountant. They&apos;ll be notified immediately.
                </div>
              ) : (
                messages.map((m, mi) => {
                  const isMe = m.sender_role === "client";
                  // Find the LAST outgoing (client) message that's been read
                  // by staff — that's where we show "Seen at X" so we don't
                  // clutter every bubble with a receipt.
                  const isLastReadOutgoing = isMe && m.read_by_staff && !messages.slice(mi + 1).some((n) => n.sender_role === "client");
                  return (
                    <div key={m.id} className={`flex flex-col ${isMe ? "items-end" : "items-start"}`}>
                      <div
                        className="max-w-[80%] px-3 py-2 text-sm"
                        style={{
                          background: isMe ? brandColor : "var(--ll-mint)",
                          color: isMe ? "white" : "var(--ll-ink)",
                        }}
                      >
                        <div className="text-[10px] uppercase tracking-widest opacity-70 mb-0.5">
                          {isMe ? "You" : (m.sender_name || "Accountant")} · {fmtDate(m.created_at)}
                        </div>
                        {m.body && <div className="whitespace-pre-wrap break-words">{m.body}</div>}
                        {m.attachments?.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {m.attachments.map((a, i) => (
                              <a
                                key={i}
                                href={`${api.defaults.baseURL || ""}/messages/attachments/download?path=${encodeURIComponent(a.storage_path)}`}
                                target="_blank" rel="noopener noreferrer"
                                className="text-[11px] flex items-center gap-1 underline"
                                data-testid={`client-msg-att-${m.id}-${i}`}
                              >
                                <Paperclip size={11} /> {a.file_name}
                              </a>
                            ))}
                          </div>
                        )}
                        {m.refs?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {m.refs.map((r, i) => (
                              <span
                                key={i}
                                className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 border"
                                style={{ borderColor: isMe ? "rgba(255,255,255,0.3)" : "var(--ll-border)" }}
                              >
                                <FileText size={10} />{r.label || r.kind}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      {isLastReadOutgoing && (
                        <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mt-1" data-testid={`client-msg-seen-${m.id}`}>
                          Seen {fmtDate(m.read_by_staff_at)}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {msgAttachments.length > 0 && (
              <div className="px-4 py-2 border-t text-xs flex flex-wrap gap-1" style={{ borderColor: "var(--ll-border)" }} data-testid="client-msg-attachments-preview">
                {msgAttachments.map((a, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[var(--ll-mint)]">
                    <Paperclip size={10} /> {a.file_name}
                    <button onClick={() => setMsgAttachments((prev) => prev.filter((_, j) => j !== i))} className="ml-1 text-[var(--ll-ink-soft)]" aria-label="Remove"><X size={10} /></button>
                  </span>
                ))}
              </div>
            )}

            <div className="p-3 border-t flex items-end gap-2" style={{ borderColor: "var(--ll-border)" }}>
              <button
                onClick={() => msgFileRef.current?.click()}
                data-testid="client-msg-attach-btn"
                className="p-2 border hover:bg-[var(--ll-mint)] shrink-0"
                style={{ borderColor: "var(--ll-border)" }}
                aria-label="Attach file"
              >
                <Paperclip size={14} />
              </button>
              <input
                ref={msgFileRef}
                type="file"
                className="hidden"
                onChange={(e) => uploadMsgAttachment(e.target.files?.[0])}
              />
              <textarea
                rows={2}
                value={msgDraft}
                onChange={(e) => setMsgDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) sendMessage(); }}
                placeholder="Type a message… ⌘/Ctrl+Enter to send"
                data-testid="client-msg-input"
                className="flex-1 px-3 py-2 border text-sm resize-none"
                style={{ borderColor: "var(--ll-border)" }}
              />
              <button
                onClick={sendMessage}
                disabled={msgSending || (!msgDraft.trim() && msgAttachments.length === 0)}
                data-testid="client-msg-send"
                className="p-2 text-white disabled:opacity-50 shrink-0"
                style={{ background: brandColor }}
                aria-label="Send"
              >
                <Send size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCell({ label, value, tone, testid }) {
  const t = TONE_CLASSES[tone];
  return (
    <div
      className="bg-white border p-3"
      style={{ borderColor: "var(--ll-border)" }}
      data-testid={testid}
    >
      <div className="flex items-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.dot }} />
        <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">{label}</div>
      </div>
      <div className="font-display font-bold text-2xl mt-1" style={{ color: t.text }}>{value}</div>
    </div>
  );
}
